import React, { useState, useEffect } from 'react'
import Head from 'next/head'
import { motion } from 'framer-motion'

export const Faq = ({
  questionsAnswers,
  showSearch = true,
  title = "Frequently Asked Questions",
  subtitle = "",
  animateDelay = 0,
  // Set false when another page already emits these FAQs as FAQPage, so the
  // same questions are not marked up on two URLs.
  jsonLd = true
}) => {
  const [filteredFaqs, setFilteredFaqs] = useState(questionsAnswers)
  const [searchText, setSearchText] = useState("")
  const [expandedFaq, setExpandedFaq] = useState(null)

  useEffect(() => {
    if (searchText.length > 2 && showSearch) {
      const filtered = questionsAnswers.filter(({ question, answer }) =>
        (question + answer).toLowerCase().includes(searchText.toLowerCase())
      )
      setFilteredFaqs(filtered)
    } else {
      setFilteredFaqs(questionsAnswers)
    }
  }, [searchText, questionsAnswers, showSearch])

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index)
  }

  // Generate FAQ Schema Markup
  // NOTE: always built from the full, unfiltered list so that typing in the
  // search box can never mutate the emitted JSON-LD.
  const generateFaqSchema = () => {
    const faqEntities = questionsAnswers.slice(0, 10).map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqEntities
    }
  }

  return (
    <>
      {/* FAQ Schema Markup */}
      {jsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(generateFaqSchema())
            }}
          />
        </Head>
      )}

      {/* FAQ Section */}
      <div className="bg-white py-6 md:py-16 px-2 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-5 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">{title}</h2>
            {subtitle && (
              <p className="text-lg text-gray-600">{subtitle}</p>
            )}
          </div>

          {/* Search Box */}
          {showSearch && (
            <div className="relative max-w-lg mx-auto mb-8">
              <input
                id="faq-search"
                type="search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search FAQs..."
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 ring-amber-300 text-sm shadow-sm placeholder:text-gray-400 focus:outline-none focus:border-amber-400"
              />
              <div className="absolute left-3 top-3.5 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {filteredFaqs.map((item, index) => (
              <div
                key={index} className="bg-gray-50 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-100 transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-900">{item.question}</span>
                  <motion.span
                    animate={{ rotate: expandedFaq === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-orange-600"
                  >
                    ▼
                  </motion.span>
                </button>

                <motion.div
                  initial={false}
                  animate={{
                    height: expandedFaq === index ? "auto" : 0,
                    opacity: expandedFaq === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="text-sm px-4 pb-4 text-gray-600">
                    {item.answer}
                  </div>
                </motion.div>
              </div>
            ))}

            {filteredFaqs.length === 0 && (
              <p className="text-center text-sm text-gray-400">No matching FAQs found.</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
